<template>
  <e-chart
    v-if="hasData"
    :options="chartOptions"
    autoresize
    class="apache-echarts-default-graph"
  ></e-chart>
  <div v-else-if="loaded">
    {{ $t("No Data Available") }}
  </div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>

<script>
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import readableBytes from '@/xvisor/utilities/readableBytes';
import themeStyle from '@/xvisor/utilities/themeStyle';
import timeFormat from '@/xvisor/utilities/timeFormat';

export default {
  components: {
    SpinnerCmpt,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      series: [],
      loaded: false,
    };
  },
  watch: {
    url() {
      this.httpGet();
    },
    timeRange() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  computed: {
    hasData() {
      return this.loaded && this.series && this.series.length > 0;
    },
    toggleTooltipColor() {
      return themeStyle.styleToggle(colorPalette.white, colorPalette.tooltipBackground);
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
    allTimes() {
      return this.series.map((item) => item.points.map((obj) => obj.time));
    },
    timeSet() {
      return new Set(...this.allTimes);
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timeSet, new Date());
    },
    formatSeries() {
      return this.series.map((item) => ({
        name: item.name,
        type: 'bar',
        stack: 'bar',
        emphasis: {
          focus: 'series',
        },
        data: item.points.map(((point) => {
          const obj = {
            name: item.name,
            value: point.value,
          };
          return [point.time, point.value, obj];
        })),
      }));
    },
    chartOptions() {
      return {
        color: [
          colorPaletteShade.teal3,
          colorPaletteShade.red3,
          colorPaletteShade.green3,
          colorPaletteShade.orange3,
          colorPaletteShade.indigo3,
          colorPaletteShade.gray3,
          colorPaletteShade.yellow3,
          colorPaletteShade.blue3,
          colorPaletteShade.purple3,
          colorPaletteShade.pink3,
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: {
              width: 10,
              opacity: 0.2,
            },
          },
          formatter: (info) => `
            <div class="apache-echarts-tooltip">
              <div class="apache-echarts-tooltip-timestamp">
                ${moment(info[0].value[0]).format(this.timeSeriesFormat)}
              </div>
              ${this.tooltipInfo(info)}
            </div>
          `,
        },
        grid: {
          top: 30,
          bottom: 30,
          left: 50,
          right: 50,
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          min: this.allTimes[0][0],
          max: this.allTimes[0][this.allTimes[0].length - 1],
          offset: 10,
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => moment(value).format(this.timeSeriesFormat),
            fontSize: 10,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          show: true,
          name: 'Count',
          nameLocation: 'center',
          nameGap: 35,
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            show: true,
          },
        },
        label: {
          show: false,
        },
        series: this.formatSeries,
      };
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      this.$http
        .get(this.url, {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
            stepMinutes: granularity.granularity72(this.timeRange),
          },
        })
        .then((response) => { this.series = response.data; })
        .finally(() => { this.loaded = true; });
    },
    tooltipInfo(info) {
      let res = '';
      for (let i = 0; i < info.length; i += 1) {
        if (info[i].value[2].value > 0) {
          res += `
            ${info[i].marker} ${info[i].value[2].name}: ${info[i].value[2].value}<br>
          `;
        }
      }
      return res;
    },
    readableBytes,
  },
};
</script>
